import { isNullOrWhiteSpace } from "./stringFormatter";
import { ADV_SEARCH_OBJ_MAX_ROW } from "../redux/constants/metaData";
import { sortObjectConnectionResultsByPrimaryColumn } from "./objectConnectionSearchUtils";

/** Map filter labels to related view columns (label + field name patterns). */
const FILTER_SEARCH_GROUPS = [
  {
    labelPattern: /directive|description|work done/i,
    fieldPattern:
      /directive|descr|description|work|text|err_|note|summary|comment/i,
    extraRowKeys: ["KEYDESC", "KEY_DESC", "DOC_OBJ_DESC", "DESCRIPTION"],
  },
  {
    labelPattern: /maintenance\s*organization/i,
    fieldPattern: /maint|morg|maint_org|maintenance/i,
    extraRowKeys: [],
    strictPrimaryOnly: true,
  },
  {
    labelPattern: /supplier|vendor/i,
    fieldPattern: /supplier|vendor/i,
    extraRowKeys: [],
    strictPrimaryOnly: true,
  },
  {
    labelPattern: /fault/i,
    fieldPattern: /fault/i,
    extraRowKeys: [],
    strictPrimaryOnly: true,
  },
  {
    labelPattern: /wo number|wo_no/i,
    fieldPattern: /^wo_no$|wo_no_|work_order_no/i,
    extraRowKeys: [],
    strictPrimaryOnly: true,
  },
  {
    labelPattern: /object id|objid/i,
    fieldPattern: /objid|object_id/i,
    extraRowKeys: [],
    strictPrimaryOnly: true,
  },
  {
    labelPattern: /status/i,
    fieldPattern: /status|state/i,
    extraRowKeys: [],
  },
  {
    labelPattern: /connection|delivery|address|reported/i,
    fieldPattern: /connect|delivery|address|report/i,
    extraRowKeys: [],
  },
];

export function resolveAdvSearchFilterType(dataType, fieldName, columnLabel) {
  const upper = (dataType ?? "").toUpperCase();
  if (
    upper.includes("NUMBER") ||
    upper.includes("INTEGER") ||
    upper.includes("DECIMAL") ||
    upper.includes("NUMERIC") ||
    upper.includes("FLOAT")
  ) {
    return "number";
  }
  if (upper.includes("DATE") || upper.includes("TIME")) {
    return "date";
  }

  const field = String(fieldName ?? "").trim();
  const label = String(columnLabel ?? "").trim().toLowerCase();
  if (
    /^wo_no$|wo_no_|work_order_no|seq_no$/i.test(field) ||
    /wo number|sequence no|seq no/i.test(label)
  ) {
    return "number";
  }
  if (isAdvSearchDateColumn({ dataType, name: label, fieldName: field })) {
    return "date";
  }
  return "text";
}

function resolvePredicateFilterType(colMeta, filter) {
  return resolveAdvSearchFilterType(
    colMeta?.dataType,
    colMeta?.fieldName ?? filter?.id,
    colMeta?.name ?? filter?.name,
  );
}

function escapeSqlLiteral(value) {
  return String(value ?? "").replace(/'/g, "''");
}

function parseNumericFilterValue(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return trimmed;
}

function normalizeCellText(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function getFilterLabel(filter, columns) {
  const primary = (filter?.id ?? "").trim();
  const colDef = (columns ?? []).find(
    (c) => (c?.fieldName ?? "").trim() === primary,
  );
  return (filter?.name ?? colDef?.name ?? primary ?? "").trim().toLowerCase();
}

function getFilterGroup(filter, columns) {
  const label = getFilterLabel(filter, columns);
  return FILTER_SEARCH_GROUPS.find((g) => g.labelPattern.test(label)) ?? null;
}

function usesStrictPrimaryColumnMatch(filter, columns) {
  const group = getFilterGroup(filter, columns);
  return group?.strictPrimaryOnly === true;
}

/** Lov/view columns only — safe for dynamic SQL WHERE (no KEYDESC etc.). */
export function getServerSearchColumnIds(filter, columns) {
  const primary = (filter?.id ?? "").trim();
  const ids = new Set();
  if (primary) ids.add(primary);

  const label = getFilterLabel(filter, columns);
  const allColumns = Array.isArray(columns) ? columns : [];

  for (const group of FILTER_SEARCH_GROUPS) {
    if (!group.labelPattern.test(label)) continue;
    for (const col of allColumns) {
      const field = (col?.fieldName ?? "").trim();
      const name = (col?.name ?? "").trim();
      if (
        field &&
        (group.fieldPattern.test(field) || group.fieldPattern.test(name))
      ) {
        ids.add(field);
      }
    }
  }

  return Array.from(ids).filter(Boolean);
}

/** All columns to check client-side (includes result-only keys like KEYDESC). */
export function getClientSearchColumnIds(filter, columns) {
  const primary = (filter?.id ?? "").trim();
  if (usesStrictPrimaryColumnMatch(filter, columns)) {
    return primary ? [primary] : [];
  }

  const ids = new Set(getServerSearchColumnIds(filter, columns));
  const label = getFilterLabel(filter, columns);

  for (const group of FILTER_SEARCH_GROUPS) {
    if (!group.labelPattern.test(label)) continue;
    for (const key of group.extraRowKeys ?? []) {
      ids.add(key);
    }
  }

  return Array.from(ids).filter(Boolean);
}

/** Normalize API row keys so they align with trimmed lovColNames / filter ids. */
export function normalizeAdvSearchRow(row) {
  if (row == null || typeof row !== "object") return row;
  const normalized = {};
  for (const [key, value] of Object.entries(row)) {
    if (key === "key" || key === "__rowId") {
      normalized[key] = value;
      continue;
    }
    normalized[String(key).trim()] = value;
  }
  return normalized;
}

const ADV_SEARCH_DATETIME_VALUE_PATTERN =
  /^(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})[\sT]\d/;

const ADV_SEARCH_PLAIN_DATE_PATTERN =
  /^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{4}-\d{2}-\d{2}$|^\d{2}-\d{2}-\d{4}$/;

/** Display/input format for Advanced Search date fields (matches result grid): ISO 8601 YYYY-MM-DD. */
export function formatAdvSearchGridDate(date) {
  if (!date || Number.isNaN(date.getTime?.())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}


function formatAdvSearchDbDate(date) {
  if (!date || Number.isNaN(date.getTime?.())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${date.getFullYear()}`;
}

/** Parse grid-style (MM/DD/YYYY) or filter-style (DD-MM-YYYY) date strings. */
export function parseAdvSearchGridDate(dateStr) {
  const trimmed = String(dateStr ?? "").trim();
  if (!trimmed) return null;

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const month = parseInt(slashMatch[1], 10) - 1;
    const day = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);
    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10) - 1;
    const day = parseInt(isoMatch[3], 10);
    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashMatch) {
    const day = parseInt(dashMatch[1], 10);
    const month = parseInt(dashMatch[2], 10) - 1;
    const year = parseInt(dashMatch[3], 10);
    const parsed = new Date(year, month, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** True when a grid/search column represents a date (metadata or label). */
export function isAdvSearchDateColumn(col) {
  const dataType = String(col?.dataType ?? "").toUpperCase();
  if (dataType.includes("DATE") || dataType.includes("TIME")) return true;
  const label = String(col?.name ?? col?.fieldName ?? "").toLowerCase();
  return /\b(date|start|finish|deadline|due|expir)\b/.test(label);
}

function hasAdvSearchDatetimePortion(value) {
  if (value == null || value === "") return false;
  return ADV_SEARCH_DATETIME_VALUE_PATTERN.test(String(value).trim());
}

function looksLikeAdvSearchPlainDate(value) {
  if (value == null || value === "") return false;
  return ADV_SEARCH_PLAIN_DATE_PATTERN.test(String(value).trim());
}

/** Strip time from values like "08/18/2021 08:00:00" or "2021-08-18T08:00:00". */
export function stripAdvSearchDatetimeTime(value) {
  if (!hasAdvSearchDatetimePortion(value)) return value;
  return String(value).trim().split(/[\sT]/)[0];
}

/** Format a grid/search date value as ISO YYYY-MM-DD (matches date picker display). */
export function formatAdvSearchGridDisplayValue(value) {
  if (value == null || value === "") return value;
  const trimmed = stripAdvSearchDatetimeTime(value);
  const parsed = parseAdvSearchGridDate(trimmed);
  if (!parsed) return trimmed;
  return formatAdvSearchGridDate(parsed);
}

function findAdvSearchRowFieldKey(row, fieldName) {
  const trimmed = String(fieldName ?? "").trim();
  if (!trimmed || row == null) return null;
  if (trimmed in row) return trimmed;
  const lower = trimmed.toLowerCase();
  return Object.keys(row).find((key) => key.toLowerCase() === lower) ?? null;
}

/** Format datetime values in search result rows (removes time portion for display). */
export function formatAdvSearchRowDatetimes(row, columns = []) {
  if (row == null || typeof row !== "object") return row;

  const formatted = { ...row };
  const cols = Array.isArray(columns) ? columns : [];

  cols.forEach((col) => {
    const fieldName = col?.fieldName ?? col?.name;
    const key = findAdvSearchRowFieldKey(formatted, fieldName);
    if (!key) return;

    const value = formatted[key];
    if (value == null || value === "") return;

    if (
      isAdvSearchDateColumn(col) ||
      hasAdvSearchDatetimePortion(value) ||
      looksLikeAdvSearchPlainDate(value)
    ) {
      formatted[key] = formatAdvSearchGridDisplayValue(value);
    }
  });

  // Also handle mis-typed columns (e.g. string metadata for Early Start / Early Finish).
  for (const key of Object.keys(formatted)) {
    if (key === "key" || key === "__rowId") continue;
    const value = formatted[key];
    if (
      hasAdvSearchDatetimePortion(value) ||
      looksLikeAdvSearchPlainDate(value)
    ) {
      formatted[key] = formatAdvSearchGridDisplayValue(value);
    }
  }

  return formatted;
}

function findAdvSearchColumnMeta(columns, columnId) {
  const id = (columnId ?? "").trim().toLowerCase();
  if (!id) return null;
  return (
    (columns ?? []).find(
      (c) => (c?.fieldName ?? "").trim().toLowerCase() === id,
    ) ?? null
  );
}

/** Normalize a date filter/cell value to ISO YYYY-MM-DD for comparison with grid values. */
export function normalizeAdvSearchDateCompareValue(value) {
  const trimmed = stripAdvSearchDatetimeTime(value);
  const parsed = parseAdvSearchGridDate(trimmed);
  if (!parsed) return normalizeCellText(trimmed);
  return normalizeCellText(formatAdvSearchGridDate(parsed));
}

function advSearchDatesMatch(cellRaw, filterValue) {
  const cellNorm = normalizeAdvSearchDateCompareValue(cellRaw);
  const filterNorm = normalizeAdvSearchDateCompareValue(filterValue);
  if (!cellNorm || !filterNorm) return false;
  return cellNorm === filterNorm;
}

export function buildAdvSearchColumnPredicate(
  columnId,
  value,
  filterType,
  columnMeta,
) {
  const column = (columnId ?? "").trim();
  const val = (value ?? "").trim();
  if (!column || isNullOrWhiteSpace(val)) return "";

  const effectiveType = columnMeta
    ? resolveAdvSearchFilterType(
        columnMeta.dataType,
        columnMeta.fieldName ?? columnId,
        columnMeta.name,
      )
    : filterType;
  const sqlVal = escapeSqlLiteral(val).toLowerCase();

  if (effectiveType === "date") {
    const parsedDate = parseAdvSearchGridDate(val);
    const dataType = String(columnMeta?.dataType ?? "").toUpperCase();
    const isStringType =
      !dataType ||
      dataType.includes("STRING") ||
      dataType.includes("CHAR") ||
      dataType.includes("TEXT");
    const isDbDateType =
      (dataType.includes("DATE") || dataType.includes("TIME")) &&
      !isStringType;

    if (isDbDateType) {
      const isoVal = parsedDate ? formatAdvSearchGridDate(parsedDate) : val;
      const escaped = escapeSqlLiteral(isoVal);
      return `TRUNC(${column}) = TO_DATE('${escaped}', 'YYYY-MM-DD')`;
    }

    // VARCHAR / formatted datetime strings (e.g. Activity Early Start) are stored
    // as MM/DD/YYYY in the DB, so match against that format even though we display ISO.
    const dbVal = parsedDate ? formatAdvSearchDbDate(parsedDate) : val;
    const dbSqlVal = escapeSqlLiteral(dbVal).toLowerCase();
    return `LOWER(${column}) LIKE '%${dbSqlVal}%'`;
  }

  if (effectiveType === "number") {
    const numVal = parseNumericFilterValue(val);
    if (numVal) return `${column} = ${numVal}`;
    // Non-numeric input on numeric columns: compare string form so the filter
    // still runs (empty predicate would load unfiltered default data).
    return `LOWER(TO_CHAR(${column})) LIKE '%${sqlVal}%'`;
  }

  return `LOWER(${column}) LIKE '%${sqlVal}%'`;
}

function buildFilterPredicate(filter, columns) {
  const label = getFilterLabel(filter, columns);
  // Description-style fields: only filter on the primary view column in SQL.
  // KEYDESC and similar exist in results only, not in the view WHERE clause.
  let columnIds = getServerSearchColumnIds(filter, columns);
  if (/directive|description|work done/i.test(label)) {
    const primary = (filter?.id ?? "").trim();
    columnIds = primary ? [primary] : columnIds;
  }
  if (/maintenance\s*organization|supplier|fault/i.test(label)) {
    const primary = (filter?.id ?? "").trim();
    columnIds = primary ? [primary] : columnIds;
  }

  const parts = columnIds
    .map((id) => {
      const colMeta = findAdvSearchColumnMeta(columns, id);
      const type = resolvePredicateFilterType(colMeta, filter);
      return buildAdvSearchColumnPredicate(
        id,
        filter.value,
        type,
        colMeta,
      );
    })
    .filter(Boolean);

  if (parts.length === 0) {
    const fallbackMeta = findAdvSearchColumnMeta(columns, filter.id);
    const fallbackType = resolvePredicateFilterType(fallbackMeta, filter);
    const fallback = buildAdvSearchColumnPredicate(
      filter.id,
      filter.value,
      fallbackType,
      fallbackMeta,
    );
    return fallback || "";
  }
  if (parts.length === 1) return parts[0];
  return `(${parts.join(" OR ")})`;
}

export function buildAdvSearchWhereClause(itemCols, columns = []) {
  const predicates = [];
  const items = Array.isArray(itemCols) ? itemCols : [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (isNullOrWhiteSpace(item?.value)) continue;
    const predicate = buildFilterPredicate(item, columns);
    if (predicate) predicates.push(predicate);
  }

  if (predicates.length === 0) return "";
  return `WHERE ${predicates.join(" AND ")} AND rownum <= ${ADV_SEARCH_OBJ_MAX_ROW}`;
}

export function getAdvSearchRowCellValue(row, columnId) {
  if (row == null) return undefined;
  const key = (columnId ?? "").trim();
  if (key && row[key] != null) return row[key];
  const match = Object.keys(row).find(
    (k) => k.trim().toLowerCase() === key.toLowerCase(),
  );
  return match ? row[match] : undefined;
}

function getRowCellValue(row, columnId) {
  return getAdvSearchRowCellValue(row, columnId);
}

/** Sort advanced search rows descending by LU key fields (mruKeyNames). */
export function sortAdvSearchResultsByKeyFields(
  rows,
  mruKeyNames,
  advObjConColumns = [],
) {
  return sortObjectConnectionResultsByPrimaryColumn(
    rows,
    mruKeyNames,
    advObjConColumns,
  );
}

function rowHasExtraKeyMatch(row, filter, val) {
  const label = getFilterLabel(filter, null);
  for (const group of FILTER_SEARCH_GROUPS) {
    if (!group.labelPattern.test(label)) continue;
    for (const extraKey of group.extraRowKeys) {
      const cell = normalizeCellText(getRowCellValue(row, extraKey));
      if (cell.includes(val)) return true;
    }
  }
  return false;
}

function rowMatchesTextFilter(row, filter, columns, val) {
  const columnIds = getClientSearchColumnIds(filter, columns);
  for (const id of columnIds) {
    const cell = normalizeCellText(getRowCellValue(row, id));
    if (cell.includes(val)) return true;
  }

  if (usesStrictPrimaryColumnMatch(filter, columns)) {
    return false;
  }

  if (rowHasExtraKeyMatch(row, filter, val)) return true;

  const label = getFilterLabel(filter, columns);
  if (!/directive|description|work done/i.test(label)) {
    return false;
  }

  // Directive/description: scan remaining visible columns when metadata columns miss.
  return Object.entries(row).some(([k, v]) => {
    if (k === "key" || k === "__rowId") return false;
    if (/^objkey$|^keyref$|^objversion$/i.test(k.trim())) return false;
    return normalizeCellText(v).includes(val);
  });
}

export function matchesAdvSearchFilter(cellRaw, filter, row = null, columns = []) {
  if (isNullOrWhiteSpace(filter?.value)) return true;

  const val = normalizeCellText(filter.value);
  if (!val) return true;

  const colMeta = findAdvSearchColumnMeta(columns, filter?.id);
  const effectiveType = resolvePredicateFilterType(colMeta, filter);

  if (effectiveType === "date") {
    return advSearchDatesMatch(cellRaw, filter.value);
  }

  if (effectiveType === "number") {
    const filterNum = parseNumericFilterValue(filter.value);
    if (filterNum != null) {
      const cellNum = parseNumericFilterValue(cellRaw);
      if (cellNum != null) return cellNum === filterNum;
      return normalizeCellText(cellRaw) === normalizeCellText(filter.value);
    }
    return normalizeCellText(cellRaw).includes(val);
  }

  if (row) {
    return rowMatchesTextFilter(row, filter, columns, val);
  }

  return normalizeCellText(cellRaw).includes(val);
}

export function applyAdvSearchClientFilters(rows, filterItems, columns = []) {
  const data = Array.isArray(rows) ? rows : [];
  const filters = Array.isArray(filterItems) ? filterItems : [];
  if (filters.length === 0) return data;

  return data.filter((row) =>
    filters.every((filter) => {
      const columnId = (filter.id ?? "").trim();
      const cellRaw = getRowCellValue(row, columnId);
      return matchesAdvSearchFilter(cellRaw, filter, row, columns);
    }),
  );
}

/** Merge API-filtered rows with matches from the last unfiltered snapshot (by row key). */
/** Apply active filters to API rows + last unfiltered snapshot (used when API returns no rows). */
export function resolveAdvSearchResultRows(
  mapped,
  filterItems,
  columns,
  lastUnfilteredRows,
  mruKeyNames = [],
) {
  const sortedMapped = sortAdvSearchResultsByKeyFields(
    mapped,
    mruKeyNames,
    columns,
  );
  const hasActiveFilters = Array.isArray(filterItems) && filterItems.length > 0;
  if (!hasActiveFilters) {
    return { data: sortedMapped, updateSnapshot: true };
  }

  const fromApi = applyAdvSearchClientFilters(sortedMapped, filterItems, columns);
  if (sortedMapped.length > 0) {
    return {
      data: sortAdvSearchResultsByKeyFields(fromApi, mruKeyNames, columns),
      updateSnapshot: false,
    };
  }

  const fromSnapshot = applyAdvSearchClientFilters(
    lastUnfilteredRows,
    filterItems,
    columns,
  );
  return {
    data: sortAdvSearchResultsByKeyFields(fromSnapshot, mruKeyNames, columns),
    updateSnapshot: false,
  };
}

export function mergeAdvSearchResults(apiRows, snapshotRows) {
  const merged = new Map();
  const add = (row) => {
    if (row == null) return;
    const key =
      row.key != null && String(row.key).trim() !== ""
        ? String(row.key).trim()
        : JSON.stringify(row);
    merged.set(key, row);
  };
  (Array.isArray(apiRows) ? apiRows : []).forEach(add);
  (Array.isArray(snapshotRows) ? snapshotRows : []).forEach(add);
  return Array.from(merged.values());
}
